import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/redux/actions/toast-actions";
import { useDispatch } from "react-redux";
import { toggleCommissionPayout } from "@/services/pricing-config.service";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function CommissionPayoutActionsPopover({ payout, onEditClick }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [toggleOpen, setToggleOpen] = useState(false);
  const isActive = payout?.isActive;
  const isOpen = Boolean(anchorEl);

  console.log(payout);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () =>
      toggleCommissionPayout({ id: payout.id, isActive: !isActive }),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Updated successfully", "success"));
      queryClient.invalidateQueries({ queryKey: ["all-commission-payouts"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update", "error"));
    },
  });

  const handleToggleClick = () => {
    handleClose();
    setToggleOpen(true);
  };

  const handleDialogClose = () => {
    setToggleOpen(false);
    toggleMutation.reset();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5 },
          },
        }}
      >
        <PopoverContent>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onEditClick(payout);
            }}
          >
            Update
          </StyledMenuItem>

          <Divider sx={{ my: 0.5 }} />

          {isActive ? (
            <StyledMenuItem onClick={handleToggleClick} danger>
              Deactivate Payout
            </StyledMenuItem>
          ) : (
            <StyledMenuItem onClick={handleToggleClick}>
              Activate Payout
            </StyledMenuItem>
          )}
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={toggleOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate Payout" : "Activate Payout"}
        description={
          isActive
            ? `Are you sure you want to deactivate "${payout?.name ?? "this payout"}"?`
            : `Are you sure you want to activate "${payout?.name ?? "this payout"}"?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />
    </>
  );
}
